import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ManualCondition, PricingResult } from '@asm/features/sync/services/pricing/pricing.interfaces';
import { PricingDTO } from '@asm/features/quick-order/models/pricing.model';
import { OfflineHelpersService } from '@asm/features/sync/interceptor/offline-helpers.service';
import {
  DefaultLocalPricingService,
  serializePricingSteps,
} from '@asm/features/sync/services/pricing/default-local-pricing.service';
import { DefaultLocalPromotionEngineService } from '@asm/features/sync/services/promotion/default-local-promotion-engine.service';
import { CartRepository } from '@asm/features/sync/repositories/cart.repository';
import { PaymentConditionRepository } from '@asm/features/sync/repositories/payment-condition.repository';
import { T685Repository } from '@asm/features/sync/repositories/t685.repository';
import { Cart, CartEntry } from '@asm/features/quick-order/models/cart.model';
import { CodedEntity } from '@asm/features/sync/models/coded-entity.model';

const DISCOUNT_CONDITION_CODE = 'ZDPC';
const SURCHARGE_CONDITION_CODE = 'ZAMP';
const PAYMENT_CONDITION_CODE = 'ZCPG';

/**
 * Handler offline para cálculo de preço local.
 * Cobre: GET /pricing/calculate
 *
 * Integra o motor de promoções offline para que descontos automáticos e
 * de cupom sejam aplicados também fora do carrinho (ex: listagem de produtos),
 * espelhando o comportamento do PricingController online.
 *
 * O header `x-cart-id` pode ser passado para usar o contexto do carrinho
 * ativo (cupom, excludedPromotions) na avaliação de promoções.
 */
@Injectable({ providedIn: 'root' })
export class PricingOfflineHandler {
  private readonly helper = inject(OfflineHelpersService);
  private readonly localPricingService = inject(DefaultLocalPricingService);
  private readonly promotionEngineService = inject(DefaultLocalPromotionEngineService);
  private readonly cartRepo = inject(CartRepository);
  private readonly paymentConditionRepo = inject(PaymentConditionRepository);
  private readonly t685Repo = inject(T685Repository);

  canHandle(req: HttpRequest<unknown>): boolean {
    return req.method === 'GET' && req.url.includes('/pricing/calculate');
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const productCode = req.params.get('product') ?? '';
    const qty = Number(req.params.get('qty') ?? '1');
    const b2bUnitId = req.headers.get('x-b2bunit-id') ?? '';
    const warehouseCode = req.params.get('warehouse') ?? req.params.get('warehouseCode') ?? undefined;
    const cartId = req.headers.get('x-cart-id') ?? undefined;
    // documentTypeCode aligns the pricing context with the selected document type,
    // matching the same tax rules as when the product is inside the cart.
    const documentTypeCode = req.params.get('documentType') ?? undefined;

    // Load cart context for promotion evaluation (mirrors online PricingController.prepareContext())
    let cart = await this.resolveCart(cartId);

    // If a documentTypeCode was provided by the client and the resolved cart does not
    // have one (or has a different one), overlay it so the pricing context is consistent
    // with what the user has selected in the order form.
    if (documentTypeCode && cart.documentTypeCode !== documentTypeCode) {
      cart = { ...cart, documentTypeCode };
    }

    // Build payment condition manual conditions from cart (mirrors online PricingController)
    const paymentConditionManualConditions = await this.buildPaymentConditionManualConditions(cart);

    // Build a minimal dummy entry (no excludedPromotions) for standalone price lookups
    const dummyEntry: CartEntry = {
      id: '',
      entryNumber: 0,
      productId: productCode,
      productCode,
      productEan: '',
      productName: productCode,
      qty,
      warehouseCode: warehouseCode ?? '',
      grossWeigh: 0,
      basePrice: { value: 0, formattedValue: '' },
      totalPrice: { value: 0, formattedValue: '' },
      stockAllocations: [],
      excludedPromotions: [],
    };

    // Get promotion conditions from offline engine (mirrors online promotion engine integration)
    const allT685 = await firstValueFrom(this.t685Repo.getAll());
    const promoConditions = await this.promotionEngineService.getPromotionConditions(
      productCode,
      b2bUnitId,
      cart,
      dummyEntry,
      allT685,
    );

    const result = await this.localPricingService.getPrice(productCode, qty, b2bUnitId, warehouseCode, [
      ...promoConditions,
      ...paymentConditionManualConditions,
    ]);

    return new HttpResponse({ status: 200, body: this.toPricingDto(result) });
  }

  buildManualConditions(discount?: number, surcharge?: number): ManualCondition[] {
    const conditions: ManualCondition[] = [];

    if ((discount ?? 0) > 0) {
      conditions.push({
        condition: DISCOUNT_CONDITION_CODE,
        conditionRate: discount ?? 0,
        conditionUnit: '%',
        calculationType: 'A',
        conditionClass: 'A',
      });
    }

    if ((surcharge ?? 0) > 0) {
      conditions.push({
        condition: SURCHARGE_CONDITION_CODE,
        conditionRate: surcharge ?? 0,
        conditionUnit: '%',
        calculationType: 'A',
        conditionClass: 'B',
      });
    }

    return conditions;
  }

  createEmptyPricingResult(): PricingResult {
    return {
      qty: 1,
      baseValue: 0,
      baseTotal: 0,
      totalValue: 0,
      finalPrice: 0,
      finalTotal: 0,
      discount: 0,
      totalDiscount: 0,
      discountPercent: 0,
      surcharge: 0,
      totalSurcharge: 0,
      surchargePercent: 0,
      tax: 0,
      totalTax: 0,
      st: 0,
      ipi: 0,
      icms: 0,
      calculationDate: new Date(),
      context: {},
      formatedPrice: this.helper.formatCurrency(0),
      formatedFinalPrice: this.helper.formatCurrency(0),
      formatedDiscount: this.helper.formatCurrency(0),
      formatedTotalDiscount: this.helper.formatCurrency(0),
      formatedSurcharge: this.helper.formatCurrency(0),
      formatedTotalSurcharge: this.helper.formatCurrency(0),
      formatedST: this.helper.formatCurrency(0),
      formatedIPI: this.helper.formatCurrency(0),
      formatedICMS: this.helper.formatCurrency(0),
      currency: 'BRL',
      steps: [],
    };
  }

  /**
   * Converte discountRate/interestRate da condição de pagamento em ManualCondition[]
   * usando o código dedicado ZCPG (espelha PricingController do backend).
   */
  private async buildPaymentConditionManualConditions(cart: Cart): Promise<ManualCondition[]> {
    if (!cart.paymentConditionCode) return [];

    const paymentCondition = (await firstValueFrom(this.paymentConditionRepo.getByKey(cart.paymentConditionCode))) as
      | CodedEntity
      | undefined;

    if (!paymentCondition) return [];

    const conditions: ManualCondition[] = [];
    const discountRate = paymentCondition['discountRate'] ? Number(paymentCondition['discountRate']) : 0;
    const interestRate = paymentCondition['interestRate'] ? Number(paymentCondition['interestRate']) : 0;

    if (discountRate > 0) {
      conditions.push({
        condition: PAYMENT_CONDITION_CODE,
        conditionRate: discountRate,
        conditionUnit: '%',
        calculationType: 'A',
        conditionClass: 'A',
      });
    }

    if (interestRate > 0) {
      conditions.push({
        condition: PAYMENT_CONDITION_CODE,
        conditionRate: -interestRate,
        conditionUnit: '%',
        calculationType: 'A',
        conditionClass: 'A',
      });
    }

    return conditions;
  }

  /**
   * Resolve the cart to use as context for promotion evaluation.
   * Mirrors online `PricingController.prepareContext()` which loads the cart by code.
   * Falls back to the active cart, then an empty cart stub.
   */
  private async resolveCart(cartId?: string): Promise<Cart> {
    if (cartId) {
      const byKey = await firstValueFrom(this.cartRepo.getByKey(cartId));
      if (byKey) return byKey;
    }

    const active = await firstValueFrom(this.cartRepo.getActive());
    if (active) return active;

    // Empty stub — no coupon, no excludedPromotions
    return {
      code: '',
      entries: [],
      totalICMS: 0,
      totalICMSFormatted: '',
    };
  }

  private toPricingDto(result: PricingResult): PricingDTO {
    return {
      currency: result.currency,
      qty: result.qty,
      price: result.baseValue,
      formatedPrice: result.formatedPrice,
      discount: result.discount,
      formatedDiscount: result.formatedDiscount,
      discountPercent: result.discountPercent,
      totalDiscount: result.totalDiscount,
      formatedTotalDiscount: result.formatedTotalDiscount,
      totalSurcharge: result.totalSurcharge,
      formatedTotalSurcharge: result.formatedTotalSurcharge,
      st: result.st,
      formatedST: result.formatedST,
      ipi: result.ipi,
      formatedIPI: result.formatedIPI,
      icms: result.icms,
      formatedICMS: result.formatedICMS,
      finalPrice: result.finalPrice,
      formatedFinalPrice: result.formatedFinalPrice,
      // Serialize pricing steps for price formation component (mirrors online pricingDTO.conditions)
      conditions: result.steps.length > 0 ? serializePricingSteps(result.steps, result.currency) : undefined,
    };
  }
}
